/**
 * First-challenge tour — pure step derivation. The visible step is a function of real app state, so an
 * off-script action (Back, removing a pick, switching market) just re-derives the correct step; there is
 * no advance-on-tap reconciliation. A small `beat` index sequences the multiple coach beats that share a
 * single screen (review: slate → coins → send; arena-live: live → nav → wrap).
 * See specs/first-challenge-onboarding/spec.md (Story 4).
 */

import {
  FC_STEP_CONFIG,
  FC_SHOWN_STEPS,
  FC_REVIEW_BEATS,
  FC_ARENA_LIVE_BEATS,
  type FcTourStep,
  type FcStepConfig,
} from '../constants/firstChallengeTour.constants';

export type FcCreateStep = 'rival' | 'build' | 'review' | 'success' | null;

export interface FcTourInput {
  /** Current route/pathname, e.g. '/app/arena' or '/app/create'. */
  route: string;
  /** The create flow's internal step, or null when not in the create flow. */
  createStep: FcCreateStep;
  /** Number of pick slips currently selected. */
  pickCount: number;
  /** Whether the first challenge has been sent (survives the create→arena hand-off). */
  sent: boolean;
  /** Beat index within a multi-beat screen (review / arena-live). Clamped to the sequence. */
  beat: number;
}

export interface DerivedTourStep extends FcStepConfig {
  progress: { index: number; total: number } | null;
  /** Whether the coach card should be shown (false for the auto celebrate beat). */
  showCoach: boolean;
}

function clampIndex(i: number, len: number): number {
  if (!Number.isFinite(i) || i < 0) return 0;
  if (i > len - 1) return len - 1;
  return Math.floor(i);
}

function isArenaRoute(route: string): boolean {
  return route.includes('/app/arena') || route === '/app' || route === '/app/';
}

/** Derive the tour step KEY from app state, or null when the user is off the tour path (hide coach). */
export function deriveTourStepKey(input: FcTourInput): FcTourStep | null {
  const { route, createStep, pickCount, sent, beat } = input;

  if (route.includes('/app/create')) {
    if (createStep === 'success') return 'celebrate';
    if (createStep === 'rival') return 'rival';
    if (createStep === 'build') return pickCount > 0 ? 'review' : 'odds';
    if (createStep === 'review') return FC_REVIEW_BEATS[clampIndex(beat, FC_REVIEW_BEATS.length)];
    // On create with no known step yet — nothing to point at.
    return null;
  }

  if (isArenaRoute(route)) {
    if (sent) return FC_ARENA_LIVE_BEATS[clampIndex(beat, FC_ARENA_LIVE_BEATS.length)];
    return 'create';
  }

  // Any other route (e.g. the user wandered off): don't show the coach.
  return null;
}

/** Full derived step: config + progress + whether the coach card renders. Null when off-path. */
export function deriveTourStep(input: FcTourInput): DerivedTourStep | null {
  const key = deriveTourStepKey(input);
  if (!key) return null;
  const cfg = FC_STEP_CONFIG[key];
  const shownIdx = FC_SHOWN_STEPS.indexOf(key);
  return {
    ...cfg,
    progress: shownIdx >= 0 ? { index: shownIdx + 1, total: FC_SHOWN_STEPS.length } : null,
    showCoach: cfg.beatType === 'action' || cfg.beatType === 'coach',
  };
}
